import { Stack, Text, Checkbox, SimpleGrid, Group, Chip, SegmentedControl } from '@mantine/core';
import { useState, forwardRef, useImperativeHandle } from 'react';
import { FoodAutocompleteInput } from '../UI/FoodAutocompleteInput';
import { PROTEIN_SUGGESTIONS } from '../../utils/proteinSuggestions';
import { DEFAULT_PROTEIN_DIVERSITY } from '../../utils/familyProfileDefaults';

type ProteinPreferenceProps = {
  value: { favoriteProteins: string[]; avoidProteins: string[]; proteinDiversity?: "minimal" | "moderate" | "high" };
  onChange: (value: { favoriteProteins: string[]; avoidProteins: string[]; proteinDiversity?: "minimal" | "moderate" | "high" }) => void;
};

export type ProteinPreferenceRef = {
  flushInputs: () => void;
};

const COMMON_PROTEINS = [
  { name: 'Chicken', icon: '🐔' },
  { name: 'Beef', icon: '🥩' },
  { name: 'Pork', icon: '🥓' },
  { name: 'Fish', icon: '🐟' },
  { name: 'Salmon', icon: '🍣' },
  { name: 'Shrimp', icon: '🦐' },
  { name: 'Turkey', icon: '🦃' },
  { name: 'Tofu', icon: '🥡' },
  { name: 'Beans', icon: '🫘' },
  { name: 'Eggs', icon: '🥚' },
  { name: 'Cheese', icon: '🧀' },
  { name: 'Nuts', icon: '🥜' },
];

const ProteinPreferenceComponent = forwardRef<ProteinPreferenceRef, ProteinPreferenceProps>(
  ({ value, onChange }, ref) => {
    const [otherProteinInput, setOtherProteinInput] = useState('');
    const [avoidProteinInput, setAvoidProteinInput] = useState('');

    useImperativeHandle(ref, () => ({
      flushInputs: () => {
        const predefinedNames = COMMON_PROTEINS.map(p => p.name);
        let newValue = { ...value };
        let updated = false;

        // Flush other proteins (favorites) input
        if (otherProteinInput.trim()) {
          const newProteins = otherProteinInput
            .split(',')
            .map(item => item.trim())
            .filter(item => item.length > 0)
            .map(item => item.charAt(0).toUpperCase() + item.slice(1).toLowerCase())
            .filter(item => !newValue.favoriteProteins.some(p => p.toLowerCase() === item.toLowerCase()) && !predefinedNames.some(p => p.toLowerCase() === item.toLowerCase()));

          const predefinedToAdd = otherProteinInput
            .split(',')
            .map(item => item.trim())
            .filter(item => item.length > 0)
            .map(item => predefinedNames.find(p => p.toLowerCase() === item.toLowerCase()))
            .filter((protein): protein is string => Boolean(protein))
            .filter(protein => !newValue.favoriteProteins.includes(protein));

          const allNewProteins = [...predefinedToAdd, ...newProteins];
          if (allNewProteins.length > 0) {
            newValue.favoriteProteins = [...newValue.favoriteProteins, ...allNewProteins];
            newValue.avoidProteins = (newValue.avoidProteins || []).filter(avoid =>
              !allNewProteins.some(newProtein => newProtein.toLowerCase() === avoid.toLowerCase())
            );
            updated = true;
            setOtherProteinInput('');
          }
        }

        // Flush avoid proteins input
        if (avoidProteinInput.trim()) {
          const newAvoidProteins = avoidProteinInput
            .split(',')
            .map(item => item.trim())
            .filter(item => item.length > 0)
            .map(item => item.charAt(0).toUpperCase() + item.slice(1).toLowerCase())
            .filter(item => !(newValue.avoidProteins || []).some(p => p.toLowerCase() === item.toLowerCase()) && !predefinedNames.some(p => p.toLowerCase() === item.toLowerCase()));

          const predefinedToAdd = avoidProteinInput
            .split(',')
            .map(item => item.trim())
            .filter(item => item.length > 0)
            .map(item => predefinedNames.find(p => p.toLowerCase() === item.toLowerCase()))
            .filter((protein): protein is string => Boolean(protein))
            .filter(protein => !(newValue.avoidProteins || []).includes(protein));

          const allNewAvoidProteins = [...predefinedToAdd, ...newAvoidProteins];
          if (allNewAvoidProteins.length > 0) {
            newValue.avoidProteins = [...(newValue.avoidProteins || []), ...allNewAvoidProteins];
            newValue.favoriteProteins = newValue.favoriteProteins.filter(favorite =>
              !allNewAvoidProteins.some(newAvoid => newAvoid.toLowerCase() === favorite.toLowerCase())
            );
            updated = true;
            setAvoidProteinInput('');
          }
        }

        if (updated) {
          onChange(newValue);
        }
      }
    }));

  const toggleFavoriteProtein = (protein: string) => {
    const current = value.favoriteProteins;
    const currentAvoids = value.avoidProteins || [];
    
    const updated = current.includes(protein)
      ? current.filter(p => p !== protein)
      : [...current, protein];
    
    // Remove from avoid list if adding to favorites
    const updatedAvoids = updated.includes(protein)
      ? currentAvoids.filter(p => p.toLowerCase() !== protein.toLowerCase())
      : currentAvoids;
    
    onChange({ ...value, favoriteProteins: updated, avoidProteins: updatedAvoids });
  };

  const toggleAvoidProtein = (protein: string) => {
    const current = value.avoidProteins || [];
    const currentFavorites = value.favoriteProteins;
    
    const updated = current.includes(protein)
      ? current.filter(p => p !== protein)
      : [...current, protein];
    
    // Remove from favorites list if adding to avoid
    const updatedFavorites = updated.includes(protein)
      ? currentFavorites.filter(p => p.toLowerCase() !== protein.toLowerCase())
      : currentFavorites;
    
    onChange({ ...value, favoriteProteins: updatedFavorites, avoidProteins: updated });
  };

  const addCustomFavoriteProtein = (text: string) => {
    if (!text.trim()) return;

    const predefinedNames = COMMON_PROTEINS.map(p => p.name);
    const currentAvoids = value.avoidProteins || [];
    const currentFavorites = value.favoriteProteins;

    const inputProteins = text
      .split(',')
      .map(item => item.trim())
      .filter(item => item.length > 0)
      .map(item => item.charAt(0).toUpperCase() + item.slice(1).toLowerCase());
    
    let updatedFavorites = [...currentFavorites];
    let updatedAvoids = [...currentAvoids];
    
    inputProteins.forEach(inputProtein => {
      // Check if it's a predefined protein (case-insensitive)
      const matchingPredefined = predefinedNames.find(p => p.toLowerCase() === inputProtein.toLowerCase());
      
      if (matchingPredefined) {
        // Add predefined protein to favorites if not already there
        if (!updatedFavorites.includes(matchingPredefined)) {
          updatedFavorites.push(matchingPredefined);
        }
        // Remove from avoid list if it's there
        updatedAvoids = updatedAvoids.filter(avoid => avoid.toLowerCase() !== matchingPredefined.toLowerCase());
      } else {
        // It's a custom protein - add if not already in favorites
        if (!updatedFavorites.some(p => p.toLowerCase() === inputProtein.toLowerCase())) {
          updatedFavorites.push(inputProtein);
        }
        // Remove from avoid list if it's there
        updatedAvoids = updatedAvoids.filter(avoid => avoid.toLowerCase() !== inputProtein.toLowerCase());
      }
    });
    
    onChange({
      ...value,
      favoriteProteins: updatedFavorites,
      avoidProteins: updatedAvoids
    });
    setOtherProteinInput('');
  };

  const addCustomAvoidProtein = (text: string) => {
    if (!text.trim()) return;

    const predefinedNames = COMMON_PROTEINS.map(p => p.name);
    const currentAvoids = value.avoidProteins || [];
    const currentFavorites = value.favoriteProteins;

    const inputProteins = text
      .split(',')
      .map(item => item.trim())
      .filter(item => item.length > 0)
      .map(item => item.charAt(0).toUpperCase() + item.slice(1).toLowerCase());
    
    let updatedAvoids = [...currentAvoids];
    let updatedFavorites = [...currentFavorites];
    
    inputProteins.forEach(inputProtein => {
      // Check if it's a predefined protein (case-insensitive)
      const matchingPredefined = predefinedNames.find(p => p.toLowerCase() === inputProtein.toLowerCase());
      
      if (matchingPredefined) {
        // Add predefined protein to avoid if not already there
        if (!updatedAvoids.includes(matchingPredefined)) {
          updatedAvoids.push(matchingPredefined);
        }
        // Remove from favorites list if it's there
        updatedFavorites = updatedFavorites.filter(favorite => favorite.toLowerCase() !== matchingPredefined.toLowerCase());
      } else {
        // It's a custom protein - add if not already in avoid list
        if (!updatedAvoids.some(p => p.toLowerCase() === inputProtein.toLowerCase())) {
          updatedAvoids.push(inputProtein);
        }
        // Remove from favorites list if it's there
        updatedFavorites = updatedFavorites.filter(favorite => favorite.toLowerCase() !== inputProtein.toLowerCase());
      }
    });
    
    onChange({
      ...value,
      favoriteProteins: updatedFavorites,
      avoidProteins: updatedAvoids
    });
    setAvoidProteinInput('');
  };

  const removeProtein = (protein: string, type: 'favorite' | 'avoid') => {
    if (type === 'favorite') {
      onChange({ ...value, favoriteProteins: value.favoriteProteins.filter(p => p !== protein) });
    } else {
      onChange({ ...value, avoidProteins: (value.avoidProteins || []).filter(p => p !== protein) });
    }
  };

  // Separate predefined and custom proteins
  const predefinedNames = COMMON_PROTEINS.map(p => p.name);
  const predefinedFavorites = value.favoriteProteins.filter(p => predefinedNames.includes(p));
  const customFavorites = value.favoriteProteins.filter(p => !predefinedNames.includes(p));
  const predefinedAvoids = (value.avoidProteins || []).filter(p => predefinedNames.includes(p));
  const customAvoids = (value.avoidProteins || []).filter(p => !predefinedNames.includes(p));

  // Curated protein suggestions for the "Other proteins" inputs, minus anything
  // the user has already picked (as a favorite or an avoid).
  const selectedProteinKeys = new Set(
    [...value.favoriteProteins, ...(value.avoidProteins || [])].map(p => p.toLowerCase())
  );
  const proteinSuggestions = PROTEIN_SUGGESTIONS.filter(p => !selectedProteinKeys.has(p.toLowerCase()));

  return (
    <Stack gap="xl">
      {/* Favorite Proteins */}
      <Stack gap="md">
        <Text fw={500}>Favorite proteins</Text>
        
        <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="sm">
          {COMMON_PROTEINS.map((protein) => {
            const isSelected = predefinedFavorites.includes(protein.name);
            const isInAvoidList = predefinedAvoids.includes(protein.name);
            return (
              <Checkbox
                key={protein.name}
                label={<span>{protein.name}&nbsp;&nbsp;{protein.icon}</span>}
                checked={isSelected}
                onChange={() => toggleFavoriteProtein(protein.name)}
                size="sm"
                color="green"
                style={{ opacity: isInAvoidList ? 0.5 : 1 }}
                styles={{
                  root: { cursor: 'pointer' },
                  input: { cursor: 'pointer' },
                  label: { cursor: 'pointer' }
                }}
              />
            );
          })}
        </SimpleGrid>

        {/* Add custom favorite protein */}
        <FoodAutocompleteInput
          value={otherProteinInput}
          onChange={setOtherProteinInput}
          onAdd={addCustomFavoriteProtein}
          suggestions={proteinSuggestions}
          placeholder="e.g. Duck, Lamb, Quinoa..."
          color="green"
        />

        {/* Display custom favorite proteins */}
        {customFavorites.length > 0 && (
          <Group gap="xs" wrap="wrap" className="chip-cloud">
            {customFavorites.map(protein => (
              <Chip
                key={protein}
                checked={true}
                onChange={() => removeProtein(protein, 'favorite')}
                variant="filled"
                color="green"
                size="sm"
                style={{ minWidth: 'fit-content' }}
              >
                {protein}
              </Chip>
            ))}
          </Group>
        )}
      </Stack>

      {/* Proteins to Avoid */}
      <Stack gap="md">
        <Text fw={500}>Proteins to avoid</Text>
        
        <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="sm">
          {COMMON_PROTEINS.map((protein) => {
            const isSelected = predefinedAvoids.includes(protein.name);
            const isInFavoriteList = predefinedFavorites.includes(protein.name);
            return (
              <Checkbox
                key={protein.name}
                label={<span>{protein.name}&nbsp;&nbsp;{protein.icon}</span>}
                checked={isSelected}
                onChange={() => toggleAvoidProtein(protein.name)}
                size="sm"
                color="red"
                style={{ opacity: isInFavoriteList ? 0.5 : 1 }}
                styles={{
                  root: { cursor: 'pointer' },
                  input: { cursor: 'pointer' },
                  label: { cursor: 'pointer' }
                }}
              />
            );
          })}
        </SimpleGrid>

        {/* Add custom avoid protein */}
        <FoodAutocompleteInput
          value={avoidProteinInput}
          onChange={setAvoidProteinInput}
          onAdd={addCustomAvoidProtein}
          suggestions={proteinSuggestions}
          placeholder="e.g. Duck, Lamb, Quinoa..."
          color="red"
        />

        {/* Display custom avoid proteins */}
        {customAvoids.length > 0 && (
          <Group gap="xs" wrap="wrap" className="chip-cloud">
            {customAvoids.map(protein => (
              <Chip
                key={protein}
                checked={true}
                onChange={() => removeProtein(protein, 'avoid')}
                variant="filled"
                color="red"
                size="sm"
                style={{ minWidth: 'fit-content' }}
              >
                {protein}
              </Chip>
            ))}
          </Group>
        )}
      </Stack>

      {/* Protein Diversity Preference */}
      <Stack gap="md">
        <Text fw={500}>Protein variety per week</Text>
        <Text size="sm" c="dimmed">How much protein variety do you typically want in your meal plans?</Text>

        {/* Mobile: Vertical */}
        <SegmentedControl
          value={value.proteinDiversity || DEFAULT_PROTEIN_DIVERSITY}
          onChange={(val) => onChange({ ...value, proteinDiversity: val as "minimal" | "moderate" | "high" })}
          data={[
            { label: 'Minimal (1-2)', value: 'minimal' },
            { label: 'Moderate (3-4)', value: 'moderate' },
            { label: 'High (5+)', value: 'high' },
          ]}
          orientation="vertical"
          size="sm"
          color="orange"
          fullWidth
          hiddenFrom="sm"
          styles={{
            control: {
              padding: '8px 12px'
            }
          }}
        />

        {/* Desktop: Horizontal */}
        <SegmentedControl
          value={value.proteinDiversity || DEFAULT_PROTEIN_DIVERSITY}
          onChange={(val) => onChange({ ...value, proteinDiversity: val as "minimal" | "moderate" | "high" })}
          data={[
            { label: 'Minimal (1-2)', value: 'minimal' },
            { label: 'Moderate (3-4)', value: 'moderate' },
            { label: 'High (5+)', value: 'high' },
          ]}
          orientation="horizontal"
          size="sm"
          color="orange"
          visibleFrom="sm"
          styles={{
            control: {
              padding: '8px 12px'
            }
          }}
        />

        <Text size="sm" c="dimmed" ta="center" style={{ fontStyle: 'italic' }}>
          {value.proteinDiversity === "minimal" && "Keep it simple with the same proteins reused multiple times throughout the week"}
          {(value.proteinDiversity === "moderate" || !value.proteinDiversity) && "A balanced approach with variety while keeping shopping efficient"}
          {value.proteinDiversity === "high" && "Maximum variety with different proteins throughout the week"}
        </Text>
      </Stack>
    </Stack>
  );
});

ProteinPreferenceComponent.displayName = 'ProteinPreference';

export const ProteinPreference = ProteinPreferenceComponent;
